import React from 'react';
import { motion } from 'framer-motion';
import OtterCompactLogo from './OtterCompactLogo';
import { useDeviceOptimization } from '../../hooks/useDeviceOptimization';

const LoadingSkeleton = ({ className = "" }) => {
  return (
    <div className={`animate-pulse bg-surface-raised animate-shimmer ${className}`} />
  );
};

const LoadingSpinner = ({ 
  size = "default", 
  message = "", 
  showLogo = false,
  fullScreen = false,
  type = "spinner" // "spinner", "skeleton", "dots", "aura", "modern"
}) => {
  const { isMobile, animationConfig } = useDeviceOptimization();
  
  const sizeClasses = {
    small: "w-4 h-4",
    default: "w-8 h-8", 
    large: "w-12 h-12",
    xlarge: "w-16 h-16"
  };

  const containerClasses = fullScreen 
    ? "fixed inset-0 bg-surface flex items-center justify-center z-50"
    : "flex items-center justify-center";

  if (type === "skeleton") {
    return (
      <div className={`space-y-4 ${fullScreen ? "p-8 max-w-md mx-auto" : ""}`}>
        <LoadingSkeleton className="h-4 w-3/4 rounded" />
        <LoadingSkeleton className="h-4 w-1/2 rounded" />
        <LoadingSkeleton className="h-4 w-5/6 rounded" />
        <LoadingSkeleton className="h-4 w-2/3 rounded" />
      </div>
    );
  }

  if (type === "dots") {
    return (
      <div className={containerClasses}>
        <div className="flex items-center space-x-2">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-3 h-3 bg-primary rounded-full"
              animate={animationConfig.enabled ? {
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5]
              } : {}}
              transition={animationConfig.enabled ? {
                duration: isMobile ? 2 : 1.5,
                repeat: Infinity,
                delay: i * 0.2
              } : {}}
            />
          ))}
          {message && (
            <span className="ml-4 text-muted-foreground font-medium">{message}</span>
          )}
        </div>
      </div>
    );
  }

  // Modern Aura Loading Animation
  if (type === "aura" || type === "modern") {
    return (
      <div className={containerClasses}>
        <div className="relative">
          {/* Pulsating Aura Background */}
          <motion.div
            className="absolute inset-0 w-24 h-24"
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <div className="w-full h-full rounded-full bg-primary/30 blur-2xl" />
          </motion.div>
          
          {/* Second Aura Layer */}
          <motion.div
            className="absolute inset-0 w-24 h-24"
            animate={{
              scale: [1.1, 1.5, 1.1],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5
            }}
          >
            <div className="w-full h-full rounded-full bg-primary/25 blur-3xl" />
          </motion.div>

          {/* Otter Logo */}
          <motion.div
            className="relative z-10"
            animate={{
              scale: [1, 1.05, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <OtterCompactLogo className="w-16 h-16" />
          </motion.div>

          {/* Orbital Dots */}
          <div className="absolute inset-0 w-24 h-24">
            {[0, 120, 240].map((rotation, i) => (
              <motion.div
                key={i}
                className="absolute top-1/2 left-1/2 w-2 h-2"
                style={{ 
                  transformOrigin: '0 0',
                  transform: `rotate(${rotation}deg) translateX(40px) translateY(-4px)`
                }}
                animate={{
                  rotate: [rotation, rotation + 360],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear",
                  delay: i * 0.3
                }}
              >
                <div className="w-2 h-2 bg-primary rounded-full" />
              </motion.div>
            ))}
          </div>
        </div>
        
        {message && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-foreground-2 font-semibold text-center mt-8 max-w-xs"
          >
            {message}
          </motion.p>
        )}
      </div>
    );
  }

  // Default spinner (enhanced)
  return (
    <div className={containerClasses}>
      <div className="flex flex-col items-center space-y-4">
        {showLogo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: animationConfig.duration }}
            className="mb-4"
          >
            <OtterCompactLogo className="w-12 h-12" />
          </motion.div>
        )}
        
        <div className="relative">
          <motion.div
            className={`${sizeClasses[size]} border-4 border-primary/30 rounded-full`}
            animate={animationConfig.enabled ? { rotate: 360 } : {}}
            transition={animationConfig.enabled ? {
              rotate: {
                duration: isMobile ? 1.5 : 1,
                repeat: Infinity,
                ease: "linear"
              }
            } : {}}
          >
            <div className="absolute inset-0 border-4 border-transparent border-t-primary rounded-full" />
          </motion.div>
        </div>
        
        {message && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: animationConfig.duration }}
            className="text-muted-foreground font-medium text-center max-w-xs"
          >
            {message}
          </motion.p>
        )}
      </div>
    </div>
  );
};

// Spezielle Komponente für Seiten-Loading (dezent - nur drehendes Otter-Logo)
export const PageLoader = ({ message = "Seite wird geladen…" }) => {
  return (
    <div className="fixed inset-0 bg-surface flex items-center justify-center z-50">
      <div className="flex flex-col items-center space-y-6">
        {/* Dezent drehendes Otter-Logo */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <OtterCompactLogo className="w-16 h-16" />
        </motion.div>
        
        {/* Optional: Nachricht */}
        {message && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-muted-foreground font-medium text-center text-sm"
          >
            {message}
          </motion.p>
        )}
      </div>
    </div>
  );
};

// Spezielle Komponente für Content-Loading
export const ContentLoader = ({ type = "skeleton" }) => (
  <LoadingSpinner type={type} />
);

// Komponente für Button-Loading
export const ButtonLoader = () => (
  <LoadingSpinner size="small" />
);

export default LoadingSpinner;
